import { Injectable } from '@angular/core';
import { TextType } from '@models/booleanEnums.model';
import { StudySearchFilter } from '@models/studySearchFilter.model';
import { LoggingService } from '@services/logging.service';
import { KeywordsSelected } from '@models/keywordsSelected.model';
import _ from 'lodash-es';
import { FilterTypes } from '../models/filterEnums.model';
import { BooleanOperations } from '../mystudies/_study_constants';

@Injectable({
  providedIn: 'root',
})
export class BooleanSearchService {
  textMatcher = /[A-Z]/g;
  parenMatcher = /[()]/g;
  numberMatcher = /[0-9]/g;
  phraseMatcher = /[A-Z0-9"]/g;

  private operators = {
    AND: { arity: 2, jsLogic: 'and' },
    OR: { arity: 2, jsLogic: 'or' },
    NOT: { arity: 1, jsLogic: '!' },
  };

  private jsonLogicLookup = {
    and: 'AND',
    or: 'OR',
    '!': 'NOT',
  };

  constructor(private logger: LoggingService) {}

  public initializeBooleanSearch(filters: StudySearchFilter[]): string {
    this.logger.info(this.constructor.name, 'initializeBooleanSearch', 'Start');
    let booleanSearch = '';

    for (let i = 0; i < filters.length; i += 1) {
      const filter = filters[i].filter;
      if (filter.isActive) {
        if (i !== 0) {
          booleanSearch += filter.type === FilterTypes.Keywords ? ' OR ' : ' AND ';
        }
        booleanSearch += (i + 1).toString();
      }
    }
    return booleanSearch;
  }

  public initializeKeywordBooleanSearch(selectedKeywords: KeywordsSelected[], operation: BooleanOperations = BooleanOperations.Or): string {
    const keywords: string[] = [];
    for (let i = 0; i < selectedKeywords.length; i++) {
      keywords.push(`"${selectedKeywords[i].keyword}"`);
    }
    return keywords.join(` ${operation === BooleanOperations.Or ? 'OR' : 'AND'}`);
  }

  public booleanToString(rpn: string[]): string {
    this.logger.info(this.constructor.name, 'booleanToString', 'Start');
    const stack: string[] = [];
    for (let i = 0; i < rpn.length; i += 1) {
      const operator = this.operators[rpn[i]];
      if (operator) {
        if (stack.length < operator.arity) {
          throw Error('Invalid boolean formula');
        }
        stack.push(this.convertNodeToText(rpn[i], stack.splice(stack.length - operator.arity)));
      } else {
        stack.push(rpn[i]);
      }
    }
    return stack[0];
  }

  public getUniqueValues(rpn: string[]): string[] {
    const unique = rpn.filter((v, i, a) => a.indexOf(v) === i);
    return unique.filter((el) => !Object.keys(this.operators).includes(el));
  }

  public booleanToJsonLogic(rpn: string[]): any {
    this.logger.info(this.constructor.name, 'booleanToJsonLogic', 'Start');
    const stack: any[] = [];
    for (let i = 0; i < rpn.length; i += 1) {
      const operator = this.operators[rpn[i]];
      if (operator) {
        if (stack.length < operator.arity) {
          throw Error('Invalid boolean formula');
        }
        stack.push(this.convertNodeToJsonLogic(operator, stack.splice(stack.length - operator.arity)));
      } else {
        stack.push(rpn[i]);
      }
    }
    this.logger.info(this.constructor.name, 'booleanToJsonLogic', `stack[0].length:[${stack[0] ? stack[0].length : 0}]`);
    return stack[0];
  }

  public parseBooleanString(booleanString: string): any {
    this.logger.info(this.constructor.name, 'parseBooleanString', 'Start');
    const outputQueue: string[] = [];
    const operatorStack: string[] = [];
    if (booleanString == null) {
      return outputQueue;
    }

    // Remove whitespace
    if (booleanString.length > 0) {
      booleanString = booleanString.match(/(?:[^\s"]+|"[^"]*")+/g).join('');
    }

    while (booleanString.length > 0) {
      const token = this.takeUntilTypeChanges(booleanString, null);
      booleanString = booleanString.substr(token.value.length);
      if (token.type === TextType.number) {
        outputQueue.push(token.value);
      } else if (token.type === TextType.phrase) {
        // remove the double quotes
        outputQueue.push(
          token.value
            .split('')
            .filter((c) => c !== '"')
            .join('')
        );
      } else if (token.type === TextType.operator) {
        const operator = this.operators[token.value];
        if (!operator) {
          throw Error('Invalid boolean operator');
        }
        while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1] !== '(' && operator.arity > 1) {
          outputQueue.push(operatorStack.pop());
        }
        operatorStack.push(token.value);
      } else if (token.type === TextType.paren) {
        if (token.value === '(') {
          operatorStack.push(token.value);
        } else if (token.value === ')') {
          while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1] !== '(') {
            outputQueue.push(operatorStack.pop());
          }
          operatorStack.pop();
        }
      }
    }
    while (operatorStack.length > 0) {
      outputQueue.push(operatorStack.pop());
    }
    return outputQueue;
  }

  private takeUntilTypeChanges(text: string, type: TextType = null): any {
    // this.logger.info(this.constructor.name, 'takeUntilTypeChanges', 'Start');
    let returnText = '';
    let currentType = type;

    while (text.length > 0) {
      const char = text.slice(0, 1);
      text = text.substr(1);

      currentType = this.getTextType(char);
      if (type === null) {
        type = currentType;
      }
      if (type === currentType || type === TextType.phrase) {
        returnText += char;
        if (type === TextType.paren) {
          // paren is only one character (to avoid "((" cases)
          break;
        }
        if (type === TextType.phrase && returnText.split('').filter((c) => c === '"').length === 2) {
          break;
        }
        if (this.operators[returnText] !== undefined) {
          // we have found an operator
          break;
        }
      } else {
        break;
      }
    }

    return { value: returnText, type: type };
  }

  private getTextType(text: string): TextType {
    // this.logger.info(this.constructor.name, 'getTextType', 'Start');
    if (text.match(this.numberMatcher)) {
      return TextType.number;
    }
    if (text.match(this.textMatcher)) {
      return TextType.operator;
    }
    if (text.match(this.parenMatcher)) {
      return TextType.paren;
    }
    if (text.match(this.phraseMatcher)) {
      return TextType.phrase;
    }
    return TextType.invalid;
  }

  private convertNodeToText(token: string, operands: string[]) {
    this.logger.info(this.constructor.name, 'convertNodeToText', 'Start');
    if (operands.length < 1 || operands.length > 2) {
      throw Error('Invalid node');
    }
    return operands.length === 2 ? `(${operands[0]} ${token} ${operands[1]})` : `${token} ${operands[0]}`;
  }

  private convertNodeToJsonLogic(operation: any, operands: string[]) {
    this.logger.info(this.constructor.name, 'convertNodeToJsonLogic', 'Start');
    if (operands.length < 1 || operands.length > 2) {
      throw Error('Invalid node');
    }
    const returnObject = {};
    if (operands.length === 2) {
      returnObject[operation.jsLogic] = [operands[0], operands[1]];
    } else {
      returnObject[operation.jsLogic] = [operands[0]];
    }
    return returnObject;
  }

  public jsonLogicToString(json: any, delimiter = ''): string {
    this.logger.info(this.constructor.name, 'jsonLogicToString', 'Start');
    if (typeof json === 'string') {
      return `${delimiter}${json}${delimiter}`;
    }
    for (const prop in json) {
      if (Object.prototype.hasOwnProperty.call(json, prop)) {
        if (json[prop].length > 1) {
          return `(${json[prop].map((v) => this.jsonLogicToString(v, delimiter)).join(` ${this.jsonLogicLookup[prop]} `)})`;
        } else {
          return `${this.jsonLogicLookup[prop]} ${this.jsonLogicToString(json[prop][0], delimiter)}`;
        }
      }
    }
    return '';
  }

  public addFilter(id: string, json: any, operation: BooleanOperations = BooleanOperations.And): any {
    this.logger.info(this.constructor.name, 'addFilter', 'Start');
    if (json) {
      if (operation === BooleanOperations.And) {
        return {
          and: [json, id],
        };
      } else {
        return {
          or: [json, id],
        };
      }
    } else {
      return id;
    }
  }

  public removeFilter(id: string, json: any, reindex = false): any {
    this.logger.info(this.constructor.name, 'removeFilter', 'Start');
    if (!json) {
      return json;
    }
    if (typeof json === 'string' && json.length !== 1) {
      if (reindex && !_.isNaN(json) && +json > +id) {
        return (+json - 1).toString();
      } else {
        return json === id ? null : json;
      }
    }
    if (json.length === 1) {
      // This is a leaf node
      if (json === id) {
        return null;
      } else {
        if (reindex && +json > +id) {
          return (+json - 1).toString();
        }
        return json;
      }
    }
    for (const prop in json) {
      if (Object.prototype.hasOwnProperty.call(json, prop)) {
        if (json[prop].indexOf(id) > -1) {
          const remainderArray = json[prop].filter((v) => v !== id);
          if (remainderArray.length === 1) {
            return this.removeFilter(id, remainderArray[0], reindex);
          } else {
            return remainderArray.map((n) => this.removeFilter(id, n, reindex));
          }
        } else {
          json[prop] = json[prop].map((n) => this.removeFilter(id, n, reindex));
          // Filter out empty Arrays
          if (json[prop].filter((a) => !Array.isArray(a) || a.length > 0).length !== json[prop].length) {
            const notArrayJsonProp = json[prop].find((a) => !Array.isArray(a));
            json = json[prop].find((a) => a.length > 0);
            if (json === undefined) {
              json = notArrayJsonProp;
            }
          }
          return json;
        }
      }
    }
    return json;
  }
}
